/**
 * HelpInfo — (i) icon that shows tooltip from the help dictionary.
 *
 * Usage:
 *   <HelpInfo controlId="customer_name" controlName="Customer Name" />
 *
 * - Rendered only when HelpContext.iconsVisible is true (toggle via Ctrl+H).
 * - Tooltip is rendered via React Portal to document.body so it is never
 *   clipped by ancestor overflow:hidden and always sits above modals/dialogs.
 * - If canEdit=true (sys-admin/SUPER_ADMIN): shows edit pencil on hover.
 * - Saves back via POST /api/help/:project/:form/:controlId
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useHelpContext } from '../../context/HelpContext';
import { HelpCircle, Pencil, Check, X, Wand2, Loader } from 'lucide-react';
import { API_BASE as API_URL } from '../../lib/api';

const TOOLTIP_Z = 100001; // above modals (1100), toasts (1200), dev inspector (99999)

export function HelpInfo({ controlId, controlName, context, style }) {
  const { projectName, formName, canEdit, helpDict, refreshDict, iconsVisible } = useHelpContext();
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const iconRef = useRef(null);
  const tipRef = useRef(null);

  const helpText = helpDict[controlId] || '';

  useEffect(() => {
    if (editing) setEditText(helpText);
  }, [editing, helpText]);

  // Position tooltip relative to anchor every time it opens / on resize / scroll
  useLayoutEffect(() => {
    if (!show || !iconRef.current) return;
    const update = () => setAnchorRect(iconRef.current.getBoundingClientRect());
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [show]);

  // Close on outside click
  useEffect(() => {
    if (!show && !editing) return;
    const handler = (e) => {
      const inIcon = iconRef.current && iconRef.current.contains(e.target);
      const inTip = tipRef.current && tipRef.current.contains(e.target);
      if (!inIcon && !inTip) {
        setShow(false);
        setEditing(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [show, editing]);

  const handleSave = async () => {
    if (!formName) return;
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/help/${encodeURIComponent(projectName)}/${encodeURIComponent(formName)}/${encodeURIComponent(controlId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ helpText: editText }),
      });
      await refreshDict();
      setEditing(false);
      setShow(false);
    } catch { /* silent */ }
    setSaving(false);
  };

  const handleAIGenerate = async () => {
    setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/help/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controlId, controlName, formName, projectName, context }),
      });
      const json = await res.json();
      if (json.success) setEditText(json.helpText);
    } catch { /* silent */ }
    setAiLoading(false);
  };

  if (!controlId || !iconsVisible) return null;

  // Compute tooltip position (prefer above anchor; flip below if not enough room)
  let tipPos = null;
  if (show && anchorRect) {
    const TIP_W = 260;
    const TIP_H = 140;
    const margin = 10;
    let left = anchorRect.left + anchorRect.width / 2 - TIP_W / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - TIP_W - 8));
    let top = anchorRect.top - TIP_H - margin;
    let pointDown = true;
    if (top < 8) {
      top = anchorRect.bottom + margin;
      pointDown = false;
    }
    tipPos = { left, top, pointDown, anchorCx: anchorRect.left + anchorRect.width / 2 };
  }

  return (
    <>
      <span ref={iconRef} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: 4, ...style }}>
        <button
          type="button"
          data-testid={`help-icon-${controlId}`}
          onClick={(e) => { e.stopPropagation(); setShow((s) => !s); setEditing(false); }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '1px 2px',
            color: helpText ? '#2563eb' : '#94a3b8',
            display: 'inline-flex',
            alignItems: 'center',
          }}
          title={helpText || 'No help text yet'}
        >
          <HelpCircle size={13} />
        </button>
      </span>

      {show && tipPos && createPortal(
        <div
          ref={tipRef}
          data-testid={`help-tooltip-${controlId}`}
          role="tooltip"
          style={{
            position: 'fixed',
            top: tipPos.top,
            left: tipPos.left,
            width: 260,
            background: '#ffffff',
            color: '#0f172a',
            border: '1px solid #cbd5e1',
            borderRadius: 10,
            padding: '10px 12px',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: 1.5,
            zIndex: TOOLTIP_Z,
            boxShadow: '0 12px 36px rgba(15,23,42,0.18), 0 2px 6px rgba(15,23,42,0.08)',
            fontFamily: "'Inter', system-ui, sans-serif",
          }}
        >
          {/* Control name header */}
          <div style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4 }}>
            {controlName || controlId}
          </div>

          {editing ? (
            <div>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={3}
                autoFocus
                style={{
                  width: '100%',
                  background: '#f8fafc',
                  color: '#0f172a',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  padding: '6px 8px',
                  fontSize: 12,
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
                placeholder="Enter help text..."
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                <button type="button" onClick={handleAIGenerate} disabled={aiLoading}
                  style={btn('#7c3aed')}>
                  {aiLoading ? <Loader size={11} style={{ animation: 'spin 0.8s linear infinite' }} /> : <Wand2 size={11} />}
                  AI
                </button>
                <button type="button" onClick={handleSave} disabled={saving}
                  style={btn('#16a34a')}>
                  {saving ? <Loader size={11} /> : <Check size={11} />}
                  Save
                </button>
                <button type="button" onClick={() => setEditing(false)}
                  style={btn('#64748b')}>
                  <X size={11} />
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: canEdit ? 8 : 0, color: helpText ? '#0f172a' : '#64748b', fontStyle: helpText ? 'normal' : 'italic' }}>
                {helpText || 'No help text yet'}
              </div>
              {canEdit && (
                <button type="button" onClick={() => setEditing(true)}
                  style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: '#475569', display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600 }}>
                  <Pencil size={10} /> Edit
                </button>
              )}
            </div>
          )}

          {/* Pointer */}
          <div
            style={{
              position: 'absolute',
              left: Math.max(12, Math.min(tipPos.anchorCx - tipPos.left - 5, 260 - 12)),
              [tipPos.pointDown ? 'bottom' : 'top']: -6,
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              [tipPos.pointDown ? 'borderTop' : 'borderBottom']: '6px solid #ffffff',
              filter: tipPos.pointDown ? 'drop-shadow(0 2px 1px rgba(15,23,42,0.08))' : 'drop-shadow(0 -2px 1px rgba(15,23,42,0.08))',
            }}
          />
        </div>,
        document.body,
      )}
    </>
  );
}

function btn(bg) {
  return {
    background: bg,
    border: 'none',
    borderRadius: 5,
    padding: '3px 8px',
    cursor: 'pointer',
    color: 'white',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 3,
    fontSize: 10,
    fontWeight: 600,
  };
}
